import { io, Socket } from 'socket.io-client'
import { liveAppUrl } from '../const'

class SocketService {
  private static instance: SocketService
  private socket: Socket | null = null
  private listeners: Map<string, Set<(data: any) => void>> = new Map()
  private isConnecting = false
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5

  private constructor() {}

  static getInstance(): SocketService {
    if (!SocketService.instance) {
      SocketService.instance = new SocketService()
    }
    return SocketService.instance
  }

  getSocket(): Socket | null {
    return this.socket
  }

  isConnected(): boolean {
    return this.socket?.connected || false
  }

  async connect(userId: string): Promise<Socket | null> {
    if (this.socket?.connected) {
      return this.socket
    }

    if (this.isConnecting) {
      // Attendre la connexion en cours
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (!this.isConnecting) {
            clearInterval(checkInterval)
            resolve(this.socket)
          }
        }, 100)
      })
    }

    this.isConnecting = true

    return new Promise((resolve) => {
      this.socket = io(liveAppUrl, {
        auth: { userId },
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: Infinity,
        transports: ['websocket', 'polling'],
        path: '/ws',
        forceNew: false
      })

      this.socket.on('connect', () => {
        console.log('Chat Socket connecté globalement')
        this.isConnecting = false
        this.reconnectAttempts = 0
        this.socket?.emit('saveConnection', { userId })
        resolve(this.socket)
      })

      this.socket.on('connect_error', (error) => {
        console.error(' Erreur de connexion socket:', error)
        this.reconnectAttempts++
        
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
          this.isConnecting = false
          resolve(null)
        }
      })

      this.socket.on('disconnect', (reason) => {
        console.log('Chat Socket déconnecté:', reason)
      })

      // Timeout de connexion
      setTimeout(() => {
        if (this.isConnecting) {
          this.isConnecting = false
          resolve(null)
        }
      }, 10000)
    })
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect()
      this.socket = null
    }
    this.isConnecting = false
  }

  on(event: string, callback: (data: any) => void): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    this.listeners.get(event)?.add(callback)
    
    if (this.socket) {
      this.socket.on(event, callback)
    }
  }

  off(event: string, callback: (data: any) => void): void {
    this.listeners.get(event)?.delete(callback)
    if (this.socket) {
      this.socket.off(event, callback)
    }
  }

  emit(event: string, data: any): void {
    if (this.socket?.connected) {
      this.socket.emit(event, data)
    } else {
      console.warn(`ChatSocket non connecté, impossible d'émettre ${event}`)
    }
  }
}

export const socketService = SocketService.getInstance()